<div class="shrink-0 flex items-center gap-[0.5rem] px-[1.25rem] pt-[0.75rem]"
     [formGroup]="history.filterForm">
  <p-checkbox class="flex" inputId="dialog-event-history-user-only"
              formControlName="userCommentsOnly"
              [binary]="true">
    <ng-template #checkboxicon>
      <svg-icon
        src="icons/icon-checkmark-correct.svg"
        name="icon-checkmark-correct"
        class="text-(--p-surface-0) size-[0.5rem] shrink-0">
      </svg-icon>
    </ng-template>
  </p-checkbox>

  <label class="text-[0.75rem] leading-[150%] text-(--p-gray-950) cursor-pointer"
         for="dialog-event-history-user-only"
         translate="@@fp-event-history-filter__user-only"></label>
</div>

<div class="flex flex-1 min-h-0 flex-col py-[0.75rem] pl-[0.75rem] pr-[1.25rem]">
  <p-table class="fix-flex-scroll-height table-row-hover-marker text-xs"
           tableStyleClass="w-full min-w-[50.5rem] table-fixed"
           sortField="time"
           showGridlines
           stripedRows
           rowHover
           [scrollable]="true"
           scrollHeight="flex"
           [dt]="gridLinesDatatableToken"
           [sortOrder]="sortOrder()"
           [value]="rows()"
           (onSort)="onSortChange($event)">
    <ng-template #sorticon let-order>
      <svg-icon src="icons/icon-arrow-long-down.svg"
                name="icon-arrow-long-down"
                class="text-(--p-primary-100) mt-1"
                [ngClass]="{ 'text-(--p-primary-500)': order === 1 || order === -1 }"
                [svgClass]="order === 1 ? 'rotate-180' : ''"></svg-icon>
    </ng-template>

    <ng-template #header>
      <tr class="h-[1.875rem] text-[0.625rem] leading-[150%] bg-(--p-surface-0)">
        <th pFrozenColumn
            class="w-[0.5rem] min-w-[0.5rem] max-w-[0.5rem] pointer-events-none !p-0 !border-0"></th>

        <th class="w-[12rem] rounded-tl-[0.25rem] shadow-[-10px_-10px_var(--p-surface-0)]" translate="@@shared-events-status__text"></th>

        <th class="w-[7rem]" translate="@@fp-event-history-column__changed-by"></th>

        <th class="w-[6rem]" pSortableColumn="time">
          <div class="max-w-full inline-flex items-center justify-start">
            <span translate="@@shared-date__text"></span>

            <ng-container [ngTemplateOutlet]="sorticon"
                          [ngTemplateOutletContext]="{ $implicit: sortOrder() }"></ng-container>
          </div>
        </th>

        <th translate="@@fp-event-history-column__action"></th>

        <th translate="@@shared-comment-label"></th>
      </tr>
    </ng-template>

    <ng-template #body let-row>
      <tr class="relative h-[2.5625rem] group">
        <td pFrozenColumn
            class="!w-[0.5rem] !min-w-[0.5rem] !max-w-[0.5rem] !border-0 !bg-(--p-white) items-center !p-0 pointer-events-none">
          <span class="hover-marker hidden group-hover:inline-block w-[0.25rem] h-[1rem] max-h-[90%] bg-(--p-primary-100) rounded"></span>
        </td>

        <td class="font-medium">
          <div class="flex items-center gap-[0.375rem]">
            <gc-icon-badge size="1.625rem"
                           [iconSrc]="row.iconSrc"
                           [background]="row.iconBackground"></gc-icon-badge>

            @if (row.outcomeLabel) {
              <span translate="@@fp-event-history-status__with-outcome"
                    [translateParams]="{
                      I18N_VAR_STATUS: row.statusLabel | translate,
                      I18N_VAR_OUTCOME: row.outcomeLabel | translate
                    }"></span>
            } @else {
              <span [translate]="row.statusLabel"></span>
            }
          </div>
        </td>

        <td class="font-medium !border-l-0 truncate">
          @if (row.performedBy) {
            {{ row.performedBy }}
          } @else {
            <span translate="@@fp-event-history-author__system"></span>
          }
        </td>

        <td class="font-medium !border-l-0 whitespace-nowrap">{{ row.performedAt | dayjs: dateFormat }}</td>

        <td class="font-medium !border-l-0">{{ row.action }}</td>

        <td class="font-medium !border-l-0 break-words">{{ row.comment ?? emptyValue }}</td>
      </tr>
    </ng-template>

    <ng-template #emptymessage>
      <tr class="!bg-(--p-surface-0)">
        <td pFrozenColumn
            class="!w-[0.5rem] !min-w-[0.5rem] !max-w-[0.5rem] !border-0 !bg-(--p-white) !p-0 pointer-events-none"></td>

        <td class="!py-[2rem] !text-center text-(--p-gray-500)" colspan="5">
          @if (history.userCommentsOnly()) {
            <span translate="@@fp-event-history__empty-user-only"></span>
          } @else {
            <span translate="@@fp-event-history__empty"></span>
          }
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

@let comment = commentForm.controls.comment.value;
@let commentTooLong = commentForm.controls.comment.hasError('maxlength');

<div class="shrink-0 px-[1.25rem] py-[1.25rem]"
     [formGroup]="commentForm">
  <div class="text-[0.75rem] font-semibold leading-[150%] text-(--p-primary-400)"
       translate="@@shared-comment-label"></div>

  <div class="mt-[0.5rem] relative flex flex-col rounded-[0.5rem] border-[1px] border-(--p-primary-50) p-[0.75rem]"
       [ngClass]="{
         'border-(--p-surface-300) shadow-[0px_1px_0.5rem_0px_var(--p-other-opacity-sky-20)]': isFocused(),
         '!border-(--p-red-500)': commentTooLong,
       }">
    <textarea pTextarea
              [autoResize]="true"
              class="w-full min-h-[1.5rem] max-h-[6.5rem] !overflow-y-auto resize-none !rounded-none !border-0 !bg-transparent !p-0 !text-[0.75rem] !font-semibold !leading-[150%] !shadow-none !outline-none text-(--p-gray-950) placeholder:!text-(--p-gray-300) placeholder:!text-[0.75rem] placeholder:!font-medium"
              formControlName="comment"
              [placeholder]="'@@edit-comment-placeholder' | translate"
              (focus)="isFocused.set(true)"
              (blur)="isFocused.set(false)"></textarea>

    <div class="flex items-center justify-end gap-[0.25rem] pt-[0.75rem]">
      <p-button styleClass="!p-[0.375rem_1rem_0.5rem_1rem] text-[0.75rem] font-[600]"
                [label]="'@@shared-cancel__text' | translate"
                [outlined]="true"
                [disabled]="!comment"
                (onClick)="cancelComment()"></p-button>

      <p-button styleClass="!p-[0.375rem_1rem_0.5rem_1rem] text-[0.75rem] font-[600]"
                [label]="'@@shared-save' | translate"
                [disabled]="!comment.trim() || commentTooLong"
                (onClick)="submitComment()"></p-button>
    </div>
  </div>

  @if (commentTooLong) {
    <div class="mt-[0.5rem] text-[0.75rem] font-medium leading-[150%] text-(--p-red-500)"
         translate="@@edit-comment-characters-error"
         [translateParams]="{ I18N_VAR_MAX: maxCommentLength }"></div>
  } @else {
    <div class="mt-[0.5rem] text-[0.75rem] font-medium leading-[150%] text-(--p-gray-500)"
         translate="@@edit-comment-characters"
         [translateParams]="{
           I18N_VAR_CURRENT: comment.length,
           I18N_VAR_MAX: maxCommentLength
         }"></div>
  }
</div>
